// @ts-check
/* global Tygh $ module require*/
'use_strict';

import { config } from '../../tv_utils/global_config_data.js';
import { ImageValidation } from './image_validation';

declare var $;

/**
 * UI object to bind and manage the tab visibility, paging, and buttons in the config ui.
 *
 * @property {HTMLCollectionOf<Element>} tabs elements from 'tv-tab__content';
 * @property {Object} buttons obj containing references to the prev, next, and links buttons.
 * @property {number} active_idx id of the tab that is active and visible.
 * @class
 */
export class Tabs {

    /**
     * @var {HTMLCollectionOf<HTMLElement>} tabs elements from 'tv-tab__content'.
     * @var {[key: string]: any} buttons obj containing references to the prev, next, and links buttons.
     * @var {number} active_idx id of the tab that is active and visible.
     * @var {number} tab_count_threshold is the amount of tabs where the tabs will wrap to a new line.
     * @var {string} modal_suffix of the current configuration.
     * @var {ImageValidation} validator to use when the tabs are clicked.
     * @var {boolean} reconfig denotes if the configuration is updating an existing configuration.
     * @var {boolean} disable_tabs denotes if the configuration is working on reloading.
     */
    tabs: HTMLCollectionOf<HTMLElement>;
    buttons: {[key: string]: any};
    active_idx: number = 0;
    tab_count_threshold: number = 3;
    modal_suffix: string;
    validator: ImageValidation;
    reconfig: boolean;
    disable_tabs: boolean = false;

    /**
     * Accepts the html elements to bind the controls, then sets the active (visible) tab.
     *
     * @example:
     * tabs = document.getElementsByClassName('tv-tab__content');
     * buttons = {'next': Element, 'prev': Element, 'link': HTMLCollectionOf<Element>}
     *
     * @param {HTMLCollectionOf<Element>} tabs elements from 'tv-tab__content'.
     * @param {[key: string]: any} buttons obj containing references to the prev, next, and links buttons.
     * @param {number} active_idx id of the tab that is active and visible.
     * @param {string} modal_suffix of the current configuration.
     * @param {ImageValidation} validator to use when the tabs are clicked.
     * @param {boolean} reconfig denotes if the configuration is updating an existing configuration.
     */
    constructor(tabs, buttons, active_idx, modal_suffix, validator, reconfig = false) {
        this.tabs = tabs;
        this.buttons = buttons;
        this.modal_suffix = modal_suffix;
        this.validator = validator;
        this.reconfig = reconfig;

        if (this.buttons.prev) {
            for (let i = 0; i < this.buttons.prev.length; i++) {
                this.buttons.prev[i].addEventListener('click', this.prev.bind(this), false);
            }
        }
        if (this.buttons.next) {
            for (let i = 0; i < this.buttons.next.length; i++) {
                this.buttons.next[i].addEventListener('click', this.next.bind(this), false);
            }
        }

        for (const link of this.buttons.links) {
            link.addEventListener('click', this.link_clicked.bind(this), false);
        }

        for (const mobile_link of this.buttons.mobile_links) {
            mobile_link.addEventListener('click', this.link_clicked.bind(this), false);
        }

        this.create_mobile_links();

        this.set_active(active_idx);
    }

    // TODO CSCART4 - Figure out if adding the high count class belongs here.
    /**
     * Create tab links for mobile view.
     *
     * @returns {void}
     */
    create_mobile_links() {
        // Select parent container of the tabs.
        const tabs_content = document.getElementsByClassName('tv-tabs__content');

        // Count the tabs.
        const tab_count = this.buttons.links.length;

        // If the tab count is higher than the threshold, add the high count class.
        if (tab_count > this.tab_count_threshold) {
            for (const tab_content of tabs_content) {
                tab_content.classList.add('tv-tab__high-count');
            }
        }
    }

    /**
     * Getter for the active tab.
     *
     * @public
     * @returns {number} index of the active tab.
     */
    get_active() {
        return this.active_idx;
    }

    /**
     * Event listener to handle clicks on the links in order to change the active tab.
     *
     * @listens click
     * @param {Event} e from click event.
     * @returns {void}
     */
    link_clicked(e) {
        if (!this.disable_tabs) {
            const caller = e.target || e.srcElement;
            const idx = parseInt(caller.getAttribute('data-idx'), 10);

            // Toggle content for mobile tab links.
            if (caller.classList.contains('tv-tablink__mobile')) {
                caller.classList.toggle('active');
                this.tabs[idx].style.display = 'none';
                if (idx === this.active_idx && caller.classList.contains('active')) {
                    this.tabs[idx].style.display = 'block';
                }
            }

            if (idx !== this.active_idx) {
                this.set_active(idx);
            }
        }
    }

    /**
     * Handles the next button click events.
     *
     * @listens click
     * @param {Event} e from click event.
     * @returns {void}
     */
    next(e) {
        if (!this.disable_tabs) {
            if (!this.is_hardware_only() && this.validator.do_validation) {
                this.validator.validate_images();
            }

            if (!this.reconfig) {
                const this_target = e.target;
                if (!this_target.form.checkValidity()) {
                    for (const input of this_target.form) {
                        if (!input.validity.valid) {
                            (<HTMLInputElement> document.getElementById(input.id)).reportValidity();
                            return;
                        }
                    }
                }
            }

            if (this.tabs.length - 1 > this.active_idx) {
                const next_idx = this.active_idx + 1;

                // Reconfig cannot move.
                if (!this.reconfig) {
                    if (this.buttons.links[next_idx].style.display !== 'none') {
                        this.buttons.links[next_idx].scrollIntoView();
                    }

                    if (this.buttons.mobile_links[next_idx].style.display !== 'none') {
                        this.buttons.mobile_links[next_idx].scrollIntoView();
                    }

                    // This is to account for the sticky header.
                    window.scrollBy(0, -49);
                }

                this.set_active(next_idx);
            }
        }
    }

    /**
     * Handles previous button click events.
     *
     * @listens click
     * @param {Event} e from click event.
     * @returns {void}
     */
    prev(e) {
        if (!this.disable_tabs) {
            if (!this.is_hardware_only() && this.validator.do_validation) {
                this.validator.validate_images();
            }

            const this_target = e.target;
            if (!this.reconfig && !this_target.form.checkValidity()) {
                for (const input of this_target.form) {
                    if (!input.validity.valid) {
                        (<HTMLInputElement> document.getElementById(input.id)).reportValidity();
                        return;
                    }
                }
            }

            if (this.active_idx > 0) {
                const prev_idx = this.active_idx - 1;

                // Reconfig cannot move.
                if (!this.reconfig) {
                    if (this.buttons.links[prev_idx].style.display !== 'none') {
                        this.buttons.links[prev_idx].scrollIntoView();
                    }

                    if (this.buttons.mobile_links[prev_idx].style.display !== 'none') {
                        this.buttons.mobile_links[prev_idx].scrollIntoView();
                    }

                    // This is to account for the sticky header.
                    window.scrollBy(0, -49);
                }

                this.set_active(prev_idx);
            }
        }
    }

    /**
     * Validate if a configuration is Hardware Only.
     *
     * @returns {boolean} of whether the configuration is Hardware Only or not.
     */
    is_hardware_only() {
        // Get the hardware only production codes and make sure this is not a hardware only
        // configuration before validating the images.
        const hardwareOnlyMethods = config().configurator.order_types.stripped.types.HardwareOnly;
        const orderType = (<HTMLInputElement> document.getElementById('js-configuration-order-type')).value;
        let isHardwareOnly = false;

        for (const hardwareMethod of hardwareOnlyMethods) {
            if (hardwareMethod.toLowerCase() === orderType.toLowerCase()) {
                isHardwareOnly = true;
                return isHardwareOnly;
            }
        }

        return isHardwareOnly;
    }

    /**
     * Sets the active tab to the passed tab index.
     *
     * @private
     * @param {number} idx index of the tab to set active.
     * @returns {void}
     */
    set_active(idx) {
        const active_idx = this.active_idx || 0;

        // Only update if there's more than one tab.
        if (this.tabs.length > 1) {
            // Hide the old active tab and show the new one.
            this.tabs[active_idx].style.display = 'none';
            this.tabs[active_idx].classList.add('hidden');
            this.tabs[idx].style.display = 'block';
            this.tabs[idx].classList.remove('hidden');
        }

        // If there is only one tab it never gets its style changed from
        // none to block.
        if (active_idx === idx) {
            if (this.tabs[idx]) {
                this.tabs[idx].style.display = 'block';
                this.tabs[idx].classList.remove('hidden');
            }
        }
        const dialog = $.ceDialog('get_last')[0];
        if (dialog) {
            $.ceDialog('get_last').ceDialog('reload');
        }

        // Update the UI styling if there's more than one tab (link) to update.
        if (this.buttons.links.length > 1 || this.buttons.mobile_links > 1) {
            this.buttons.links[this.active_idx].classList.remove('active');
            this.buttons.links[idx].classList.add('active');

            this.buttons.mobile_links[this.active_idx].classList.remove('active');
            this.buttons.mobile_links[idx].classList.add('active');
        }

        this.active_idx = idx;

        //  Don't try to update the buttons tabs if there's only one tab.
        if (this.tabs.length < 1) {
            return;
        }

        // Only one tab exists.
        if (this.tabs.length === 1) {
            this.buttons.links[idx].classList.add('active');
            this.buttons.mobile_links[idx].classList.add('active');
            if (this.tabs[idx].parentElement.classList.contains('js-product-tab-container')) {
                this.tabs[idx].parentElement.classList.remove('hidden');
            }

            document.getElementsByClassName('tv-button__prev')[0].classList.add('tv-display__none');
            document.getElementsByClassName('tv-button__next')[0].classList.add('tv-display__none');

        // First tab is active.
        } else if (this.active_idx === 0) {
            this.buttons.prev[this.active_idx].style.display = 'none';
            this.buttons.next[this.active_idx].style.display = 'block';
        // Last tab is active.
        } else if (this.active_idx === this.tabs.length - 1) {
            this.buttons.prev[this.active_idx].style.display = 'block';
            this.buttons.next[this.active_idx].style.display = 'none';
        // Anything between first and last.
        } else {
            this.buttons.prev[this.active_idx].style.display = 'block';
            this.buttons.next[this.active_idx].style.display = 'block';
        }
    }
}
